/**
 * Overlap + close calls for a FINISHED search, for the Activity view.
 *
 * Reads the job result (GET /api/jobs/:id), which carries the same `overlap`
 * and `nearMisses` as the live poll, so the admin can still pull those
 * creators in after the search panel was dismissed.
 *
 * Usage: <SearchJobInsights jobId={job.id} folderPublicId={job.folderId} folderName={…} />
 */
import type { JSX } from 'react';
import { useJobInsights, useSearchSchema } from './hooks';
import { nearMissesFromWiden } from './insights';
import { NearMissPanel } from './NearMissPanel';
import { OverlapPanel } from './OverlapPanel';

export function SearchJobInsights({
  jobId,
  folderPublicId,
  folderName,
}: {
  jobId: string;
  folderPublicId: string;
  folderName?: string | null;
}): JSX.Element | null {
  const job = useJobInsights(jobId);
  const { schema } = useSearchSchema();
  const data = job.data;
  if (!data || typeof data !== 'object') return null;
  const nearMisses = data.nearMisses ?? nearMissesFromWiden(data.widen ?? data.widenAnalysis, data.params ?? null);
  if (!data.overlap && !nearMisses) return null;
  return (
    <div className="space-y-2">
      <OverlapPanel overlap={data.overlap ?? null} jobId={jobId} folderPublicId={folderPublicId} folderName={folderName} />
      <NearMissPanel
        nearMisses={nearMisses}
        schema={schema}
        jobId={jobId}
        folderPublicId={folderPublicId}
        canInclude={!!data.nearMisses}
      />
    </div>
  );
}
